import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database';
import { reportError } from './ErrorReporter';
import {
  getAnalyticsOverviewRequestDiagnostics,
  getGuestRequestFailureDiagnostics,
} from './failureDiagnostics';

function reportPostgrestFailure(
  source: string,
  error: PostgrestError,
  status?: number,
  requestContext?: Record<string, unknown>
): void {
  const message = error.message ?? '';
  const diagnostics = getGuestRequestFailureDiagnostics(error);

  reportError({
    error: new Error(message || 'Supabase request failed'),
    source,
    supabaseError: error,
    network: status === undefined ? undefined : { status },
    ...(diagnostics || requestContext ? { context: { ...diagnostics, ...requestContext } } : {}),
  });
}

async function withPostgrestReporting<T extends { error: PostgrestError | null }>(
  source: string,
  promise: PromiseLike<T>
): Promise<T> {
  const result = await promise;
  if (result.error) {
    reportPostgrestFailure(source, result.error);
  }
  return result;
}

function attachPostgrestReporting<T extends { error: PostgrestError | null; status?: number }>(
  source: string,
  builder: PromiseLike<T>,
  getRequestContext?: () => Record<string, unknown>
): void {
  const originalThen = builder.then.bind(builder);
  builder.then = (<TResult1 = T, TResult2 = never>(
    onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null
  ) => {
    const requestContext = getRequestContext?.();
    const startedAt = requestContext ? performance.now() : undefined;
    return originalThen((result) => {
      if (result.error) {
        reportPostgrestFailure(
          source,
          result.error,
          result.status,
          startedAt === undefined
            ? undefined
            : {
                ...requestContext,
                analyticsElapsedMs: Math.round(Math.max(0, performance.now() - startedAt)),
              }
        );
      }
      return onfulfilled ? onfulfilled(result) : (result as unknown as TResult1);
    }, onrejected);
  }) as typeof builder.then;
}

/** RPCs that catch/re-report with richer context upstream or are telemetry itself. */
const RPC_REPORT_DEFERRAL = new Set([
  'log_answer',
  // These owners report only after their bounded retries settle. A rolled-back
  // attempt that recovers is not a second failed player action.
  'log_answer_with_dda',
  'log_game_session',
  'log_client_error',
]);
const instrumentedClients = new WeakSet<SupabaseClient<Database>>();
type FunctionsCapability = SupabaseClient<Database>['functions'];
const instrumentedFunctions = new WeakSet<FunctionsCapability>();

function instrumentFunctions(functions: FunctionsCapability): FunctionsCapability {
  if (instrumentedFunctions.has(functions)) return functions;
  const originalInvoke = functions.invoke;
  functions.invoke = ((...args: Parameters<FunctionsCapability['invoke']>) =>
    withPostgrestReporting(
      `supabase.functions.invoke:${String(args[0])}`,
      Reflect.apply(originalInvoke, functions, args)
    )) as FunctionsCapability['invoke'];
  instrumentedFunctions.add(functions);
  return functions;
}

export function instrumentSupabaseClient(client: SupabaseClient<Database>): void {
  if (instrumentedClients.has(client)) return;
  instrumentedClients.add(client);

  const originalRpc = client.rpc;
  client.rpc = ((fn, args, options) => {
    const rpcName = String(fn);
    if (RPC_REPORT_DEFERRAL.has(rpcName)) {
      return Reflect.apply(originalRpc, client, [fn, args, options]);
    }
    const builder = Reflect.apply(originalRpc, client, [fn, args, options]);
    attachPostgrestReporting(
      `supabase.rpc:${rpcName}`,
      builder,
      rpcName === 'get_partner_analytics_overview_v2'
        ? () => getAnalyticsOverviewRequestDiagnostics(args)
        : undefined
    );
    return builder;
  }) as typeof client.rpc;

  // The installed SDK returns a fresh FunctionsClient from its public getter.
  // Delegate with the canonical receiver and decorate each returned capability;
  // stable-object clients retain their original capability and identity.
  let owner: object | null = client;
  let descriptor: PropertyDescriptor | undefined;
  while (owner && !descriptor) {
    descriptor = Object.getOwnPropertyDescriptor(owner, 'functions');
    owner = Object.getPrototypeOf(owner) as object | null;
  }
  const getFunctions = descriptor?.get;
  if (getFunctions) {
    Object.defineProperty(client, 'functions', {
      ...descriptor,
      get: () => instrumentFunctions(Reflect.apply(getFunctions, client, [])),
    });
  } else {
    instrumentFunctions(client.functions);
  }
}
